import type { PeerMessage, Role } from "@duelgame/shared";
import { SignalingClient } from "./signaling";
import { WebRtcSession } from "./webrtc";

export type SessionState =
  | { phase: "idle" }
  | { phase: "connecting" }
  | { phase: "lobby"; code: string; role: Role; peerPresent: boolean }
  | { phase: "queued" }
  | { phase: "p2p"; code: string; role: Role }
  | { phase: "error"; message: string };

type Listener = (state: SessionState) => void;

export class GameSession {
  readonly signaling = new SignalingClient();
  readonly rtc = new WebRtcSession();
  private state: SessionState = { phase: "idle" };
  private listeners = new Set<Listener>();
  private peerHandlers = new Set<(msg: PeerMessage) => void>();
  private unsubSignal: (() => void) | null = null;
  private code = "";
  private role: Role | null = null;
  private offerStarted = false;

  constructor() {
    this.rtc.setIceCandidateHandler((candidate) => {
      try {
        this.signaling.send({
          type: "ice",
          candidate: JSON.stringify(candidate.toJSON()),
        });
      } catch {
        /* signaling may already be closed after P2P up */
      }
    });
    this.rtc.setConnectionLostHandler(() => {
      this.handlePeerGone();
    });
  }

  getState(): SessionState {
    return this.state;
  }

  getRole(): Role | null {
    return this.role;
  }

  onState(listener: Listener): () => void {
    this.listeners.add(listener);
    listener(this.state);
    return () => this.listeners.delete(listener);
  }

  onPeerMessage(handler: (msg: PeerMessage) => void): () => void {
    this.peerHandlers.add(handler);
    return () => this.peerHandlers.delete(handler);
  }

  private setState(state: SessionState): void {
    this.state = state;
    for (const l of this.listeners) l(state);
  }

  private ensureSignalHandlers(): void {
    if (this.unsubSignal) return;
    this.unsubSignal = this.signaling.onMessage(async (msg) => {
      switch (msg.type) {
        case "created":
        case "joined":
          this.code = msg.code;
          this.role = msg.role;
          this.offerStarted = false;
          this.setState({
            phase: "lobby",
            code: msg.code,
            role: msg.role,
            peerPresent: false,
          });
          break;
        case "matched":
          this.code = msg.code;
          this.role = msg.role;
          this.offerStarted = false;
          // Show a clearer wait state while WebRTC handshakes
          this.setState({ phase: "connecting" });
          if (msg.role === "host") {
            void this.beginOffer();
          }
          break;
        case "queued":
          this.setState({ phase: "queued" });
          break;
        case "peer_joined":
          if (this.state.phase === "lobby") {
            this.setState({ ...this.state, peerPresent: true });
          }
          if (this.role === "host") {
            void this.beginOffer();
          }
          break;
        case "offer":
          try {
            const answer = await this.rtc.acceptOffer(msg.sdp);
            this.signaling.send({ type: "answer", sdp: answer });
            await this.finishP2p();
          } catch (e) {
            this.setState({
              phase: "error",
              message: e instanceof Error ? e.message : "WebRTC offer failed",
            });
          }
          break;
        case "answer":
          try {
            await this.rtc.acceptAnswer(msg.sdp);
            await this.finishP2p();
          } catch (e) {
            this.setState({
              phase: "error",
              message: e instanceof Error ? e.message : "WebRTC answer failed",
            });
          }
          break;
        case "ice":
          await this.rtc.addIceCandidate(msg.candidate);
          break;
        case "peer_left":
          this.handlePeerGone();
          break;
        case "error":
          this.setState({ phase: "error", message: msg.message });
          break;
      }
    });

    this.rtc.onMessage((msg) => {
      for (const h of this.peerHandlers) h(msg);
    });
  }

  /** Opponent left via signaling or the peer data channel dropped. */
  private handlePeerGone(): void {
    const phase = this.state.phase;
    if (phase === "idle" || phase === "error" || phase === "connecting") return;
    if (phase === "queued") return;
    this.rtc.close();
    this.role = null;
    this.code = "";
    this.offerStarted = false;
    this.setState({ phase: "error", message: "Your opponent ran away" });
  }

  private async beginOffer(): Promise<void> {
    if (this.offerStarted || this.role !== "host") return;
    this.offerStarted = true;
    try {
      const offer = await this.rtc.createOffer();
      this.signaling.send({ type: "offer", sdp: offer });
    } catch (e) {
      this.offerStarted = false;
      this.setState({
        phase: "error",
        message: e instanceof Error ? e.message : "Failed to create offer",
      });
    }
  }

  private async finishP2p(): Promise<void> {
    try {
      await Promise.race([
        this.rtc.waitUntilOpen(),
        new Promise<never>((_, reject) => {
          window.setTimeout(() => {
            reject(
              new Error(
                "Could not establish peer link (NAT/firewall). Try again or use another network."
              )
            );
          }, 15_000);
        }),
      ]);
    } catch (e) {
      this.rtc.close();
      this.setState({
        phase: "error",
        message: e instanceof Error ? e.message : "Peer connection failed",
      });
      return;
    }
    if (!this.role || !this.code) return;
    this.setState({ phase: "p2p", code: this.code, role: this.role });
  }

  async hostLobby(): Promise<void> {
    this.resetRtc();
    this.ensureSignalHandlers();
    this.setState({ phase: "connecting" });
    try {
      await this.signaling.connect();
      this.signaling.create();
    } catch (e) {
      this.setState({
        phase: "error",
        message: e instanceof Error ? e.message : "Connection failed",
      });
    }
  }

  async joinLobby(code: string): Promise<void> {
    this.resetRtc();
    this.ensureSignalHandlers();
    this.setState({ phase: "connecting" });
    try {
      await this.signaling.connect();
      this.signaling.join(code);
    } catch (e) {
      this.setState({
        phase: "error",
        message: e instanceof Error ? e.message : "Connection failed",
      });
    }
  }

  async quickMatch(): Promise<void> {
    this.resetRtc();
    this.ensureSignalHandlers();
    this.setState({ phase: "connecting" });
    try {
      await this.signaling.connect();
      this.signaling.queue();
    } catch (e) {
      this.setState({
        phase: "error",
        message: e instanceof Error ? e.message : "Connection failed",
      });
    }
  }

  cancelQueue(): void {
    try {
      this.signaling.cancelQueue();
    } catch {
      /* ignore */
    }
    this.setState({ phase: "idle" });
  }

  leave(): void {
    // Close the peer link first so the other client unblocks even if
    // the signaling "leave" / peer_left path is slow or dropped.
    this.rtc.close();
    try {
      this.signaling.leave();
    } catch {
      /* ignore */
    }
    this.role = null;
    this.code = "";
    this.offerStarted = false;
    this.setState({ phase: "idle" });
  }

  sendPeer(msg: PeerMessage): void {
    this.rtc.send(msg);
  }

  private resetRtc(): void {
    this.rtc.close();
    this.offerStarted = false;
    this.role = null;
    this.code = "";
  }
}
